import { useEffect, useRef } from "react";
import { useTheme } from "@/components/theme/ThemeProvider";

interface Star {
  x: number;
  y: number;
  radius: number;
  alpha: number;
  speed: number;
}

export const StarBackground = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { theme } = useTheme();
  // We also need to check system preference if theme is 'system'
  const isDark = 
    theme === "dark" || 
    (theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);

  useEffect(() => {
    if (!isDark) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationFrameId: number;
    let stars: Star[] = [];
    let shootingStars: Array<{
      x: number;
      y: number;
      vx: number;
      vy: number;
      positions: Array<{ x: number; y: number; alpha: number }>;
      life: number;
      maxLife: number;
      tailLen: number;
      headRadius: number;
    }> = [];

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initStars();
    };

    const initStars = () => {
      const starCount = Math.floor((canvas.width * canvas.height) / 4000); // Density
      stars = [];
      for (let i = 0; i < starCount; i++) {
        stars.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          radius: Math.random() * 1.5,
          alpha: Math.random(),
          speed: Math.random() * 0.05 + 0.01,
        });
      }
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      ctx.fillStyle = "#ffffff";
      stars.forEach((star) => {
        ctx.globalAlpha = star.alpha;
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.radius, 0, Math.PI * 2);
        ctx.fill();

        // Twinkle effect
        star.alpha += star.speed * (Math.random() > 0.5 ? 1 : -1);
        if (star.alpha < 0.1) star.alpha = 0.1;
        if (star.alpha > 0.8) star.alpha = 0.8;
      });

      // Draw shooting stars (realistic: bright head + short particle tail)
      shootingStars.forEach((s, idx) => {
        // push head position into positions for tail
        s.positions.push({ x: s.x, y: s.y, alpha: 1 });
        if (s.positions.length > s.tailLen) s.positions.shift();

        // draw tail as connected fading stroked segments for a streak effect
        if (s.positions.length > 1) {
          ctx.lineCap = 'round';
          for (let i = 0; i < s.positions.length - 1; i++) {
            const p1 = s.positions[i];
            const p2 = s.positions[i + 1];
            const t = i / (s.positions.length - 1); // 0 older, 1 newest
            const alpha = Math.pow(t, 1.2) * 0.95; // brighter near head
            const width = Math.max(0.4, s.headRadius * (0.9 * (t + 0.1)));

            // gradient along the tiny segment for smooth fade
            const grad = ctx.createLinearGradient(p1.x, p1.y, p2.x, p2.y);
            grad.addColorStop(0, `rgba(255,255,255,${(alpha * 0.08).toFixed(3)})`);
            grad.addColorStop(1, `rgba(255,255,255,${alpha.toFixed(3)})`);

            ctx.strokeStyle = grad as unknown as string;
            ctx.lineWidth = width;
            ctx.globalAlpha = 1;
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
          }
        }

        // head as radial gradient (bright core + soft glow)
        ctx.save();
        ctx.globalCompositeOperation = 'lighter';
        ctx.shadowBlur = Math.max(6, s.headRadius * 6);
        ctx.shadowColor = 'rgba(180,220,255,0.9)';
        const g = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, s.headRadius * 6);
        g.addColorStop(0, 'rgba(255,255,255,1)');
        g.addColorStop(0.2, 'rgba(255,255,255,0.9)');
        g.addColorStop(0.45, 'rgba(255,255,255,0.25)');
        g.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = g as unknown as string;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.headRadius * 6, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();

        // bright core
        ctx.globalAlpha = 1;
        ctx.fillStyle = 'rgba(255,255,255,1)';
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.headRadius, 0, Math.PI * 2);
        ctx.fill();

        // update: move head
        s.x += s.vx;
        s.y += s.vy;
        s.life += 1;

        // remove when life exhausted or off-screen
        if (s.life > s.maxLife || s.x < -50 || s.y < -50 || s.x > canvas.width + 50 || s.y > canvas.height + 50) {
          shootingStars.splice(idx, 1);
        }
      });

      animationFrameId = requestAnimationFrame(draw);
    };

    window.addEventListener("resize", resizeCanvas);
    resizeCanvas();
    draw();

    // Spawn shooting stars occasionally (rarer, short-lived, particle tails)
    let shootTimeout: number | undefined;
    const scheduleNextShoot = () => {
      const delay = 8000 + Math.random() * 12000; // 8 - 20 seconds
      shootTimeout = window.setTimeout(() => {
        // avoid crowding: allow max 1 active shooting star
        if (shootingStars.length < 1 && Math.random() > 0.2) spawnShootingStar();
        scheduleNextShoot();
      }, delay) as unknown as number;
    };

    const spawnShootingStar = () => {
      if (!canvas) return;
      // start near bottom-left quadrant with slight variation
      const startX = Math.random() * (canvas.width * 0.12);
      const startY = canvas.height - (Math.random() * (canvas.height * 0.12) + canvas.height * 0.03);

      // aim roughly to top-right with small random offset
      const targetX = canvas.width - (Math.random() * (canvas.width * 0.12) + canvas.width * 0.02);
      const targetY = Math.random() * (canvas.height * 0.18);

      const dx = targetX - startX;
      const dy = targetY - startY;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // lifetime ~30-70 frames (short), speed derived from distance
      const frames = 30 + Math.random() * 40;
      const speed = distance / frames;
      const vx = (dx / distance) * speed;
      const vy = (dy / distance) * speed;

      const tailLen = Math.round(8 + Math.random() * 18);
      const headRadius = 1.2 + Math.random() * 1.6;

      shootingStars.push({
        x: startX,
        y: startY,
        vx,
        vy,
        positions: [],
        life: 0,
        maxLife: Math.round(frames) + 4,
        tailLen,
        headRadius,
      });
    };

    scheduleNextShoot();

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      cancelAnimationFrame(animationFrameId);
      if (typeof shootTimeout !== "undefined") clearTimeout(shootTimeout);
    };
  }, [isDark]);

  if (!isDark) return null;

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0"
      style={{ background: "transparent" }}
    />
  );
};
